// Runtime import: the bare specifier, which Vite/Rollup resolve fine via
// keycloak-js's package.json "exports" map. tsconfig.json's project-wide
// "Node" moduleResolution can't follow that same exports map (it predates
// the "exports" field spec) and misreports this as unresolvable even though
// it resolves correctly at build/dev time -- suppressed for that one line;
// the type-only import right below supplies the actual type information.
// @ts-expect-error -- see comment above; TS's classic resolver can't see the "exports" map keycloak-js uses, Vite/Rollup can.
import Keycloak from 'keycloak-js'
import type KeycloakType from 'keycloak-js/lib/keycloak.d.ts'

/**
 * Single Keycloak client instance for the whole app. Vite only exposes
 * VITE_-prefixed vars to client code (default envPrefix, see vite.config.ts),
 * so this reads the VITE_KEYCLOAK_* mirrors of the existing unprefixed
 * KEYCLOAK_* env vars rather than renaming those.
 */
const keycloak: KeycloakType = new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
})

let initPromise: Promise<boolean> | null = null
let refreshTimer: ReturnType<typeof setInterval> | null = null

/**
 * Master switch for the whole Keycloak flow, independent of whether
 * VITE_KEYCLOAK_URL/REALM/CLIENT_ID are populated -- those may still be set
 * even when Keycloak is disabled, so only this flag decides.
 */
export const isKeycloakEnabled = (): boolean =>
    import.meta.env.VITE_KEYCLOAK_ENABLED === 'true'

const startTokenRefreshLoop = () => {
    if (refreshTimer) return

    refreshTimer = setInterval(() => {
        keycloak.updateToken(30).catch(() => {
            stopTokenRefreshLoop()
            keycloak.login({ redirectUri: window.location.href })
        })
    }, 20000)
}

const stopTokenRefreshLoop = () => {
    if (refreshTimer) {
        clearInterval(refreshTimer)
        refreshTimer = null
    }
}

/**
 * Initializes Keycloak exactly once. Resolves to whether the user already
 * has a valid session (onLoad: 'check-sso' does not force a redirect, so the
 * caller decides what to do when this resolves to false).
 */
export const initKeycloak = (): Promise<boolean> => {
    if (!isKeycloakEnabled()) {
        return Promise.resolve(false)
    }

    if (!initPromise) {
        initPromise = keycloak
            .init({
                onLoad: 'check-sso',
                pkceMethod: 'S256',
                checkLoginIframe: false,
            })
            .then((authenticated: boolean) => {
                if (authenticated) {
                    startTokenRefreshLoop()
                }
                return authenticated
            })
    }

    return initPromise as Promise<boolean>
}

/**
 * Strips leftover Keycloak OAuth callback params (error/code/state/iss/
 * session_state/kc_action_status) from the current URL's query string and
 * hash. keycloak-js consumes and clears these itself on a normal
 * code-exchange or silent-check round trip, but if that round trip is
 * interrupted (e.g. the stored OAuth state already expired/was cleared by a
 * sign-out elsewhere), the params can be left sitting in the address bar.
 * A hard refresh or back-navigation then reloads with the same stale
 * "#error=login_required&state=..." fragment, which can otherwise be
 * reprocessed on every load. Safe to call unconditionally after init
 * settles: it only ever removes params, never touches app routing.
 */
export const clearKeycloakCallbackParams = () => {
    const url = new URL(window.location.href)
    const callbackKeys = [
        'error',
        'error_description',
        'error_uri',
        'code',
        'state',
        'session_state',
        'iss',
        'kc_action_status',
        'kc_action',
    ]

    let changed = false

    callbackKeys.forEach((key) => {
        if (url.searchParams.has(key)) {
            url.searchParams.delete(key)
            changed = true
        }
    })

    if (url.hash) {
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
        let hashChanged = false

        callbackKeys.forEach((key) => {
            if (hashParams.has(key)) {
                hashParams.delete(key)
                hashChanged = true
            }
        })

        if (hashChanged) {
            const remaining = hashParams.toString()
            url.hash = remaining ? `#${remaining}` : ''
            changed = true
        }
    }

    if (changed) {
        window.history.replaceState(window.history.state, '', url.toString())
    }
}

export const keycloakLogin = () => keycloak.login({ redirectUri: window.location.href })

export const keycloakLogout = (redirectUri?: string) => {
    stopTokenRefreshLoop()
    return keycloak.logout({ redirectUri: redirectUri ?? window.location.origin })
}

export const isKeycloakAuthenticated = () => Boolean(keycloak.authenticated)

export const getKeycloakToken = (): string | undefined => keycloak.token

/**
 * Attempts a token refresh (used on API 401s); if the refresh itself fails
 * (expired/invalid session), sends the user back to the Keycloak login page.
 */
export const refreshKeycloakToken = async (): Promise<string | undefined> => {
    try {
        await keycloak.updateToken(-1)
        return keycloak.token
    } catch {
        stopTokenRefreshLoop()
        await keycloakLogin()
        return undefined
    }
}

export default keycloak
